import { Vue, Component, Prop, Watch, Emit } from 'vue-property-decorator';

import TkForm from '@/../bower_components/teknisavuecomponents/components/TkForm.vue';
import Field from '@/../bower_components/teknisavuecomponents/assets/interfaces/Field';
import type { SolicitacaoAprovacao } from '@/assets/ts/controller/SolicitacaoController';
import { SolicitacaoController, TiposSolicitacao } from '@/assets/ts/controller/SolicitacaoController';
import SnackbarUtils from '@/../bower_components/teknisavuecomponents/assets/utils/SnackbarUtils';
import Messages from '@/assets/ts/utils/Messages';
import store from '@/store/store';

interface AprovacaoVariasSolicitacoesMode {
  name: string,
  save: (row: SolicitacaoAprovacao[]) => Promise<boolean> | boolean,
  condition: (row: SolicitacaoAprovacao) => boolean,
  labels?: { [name: string]: string },
  default?: boolean;
}

@Component({
  name: 'AprovacaoVariasSolicitacoes',
  components: {
    TkForm,
  },
})
class AprovacaoVariasSolicitacoes extends Vue {
  // #region [ PROPS ]
  @Prop({ default: false })
  private readonly value: boolean;

  @Prop({ default: 'A' })
  private readonly type: 'A' | 'R';

  @Prop({ default: () => [] })
  private readonly solicitacoes: any[];
  // #endregion

  // #region [ STORE ]
  // #endregion

  // #region [ EVENTS ]
  @Emit('after-save')
  private emitAfterSave() {}

  @Emit('close')
  private emitClose() {}

  @Emit('input')
  private emitInput(value: boolean): boolean {
    return value;
  }
  // #endregion

  // #region [ DATA ]
  private internalValue = false;
  private rowDefaultValue: SolicitacaoAprovacao[] = [{
    NRSOLICITACAO: null,
    NRTIPOSOLICITACAO: null,
    NRVINCULOM: null,
    NMVINCULOM: null,
    NMFASEAPROVSOLI: null,
    NMTIPOSOLICITACAO: null,
  }];
  private row: SolicitacaoAprovacao[] = { ...this.rowDefaultValue }
  private loading = false;
  private extraRowFields = ['CDSTATUS'];

  private modes: AprovacaoVariasSolicitacoesMode[] = [];

  private get fields(): Field[] {
    const fields: Field[] = [
      {
        name: 'DSOBSERVACAO',
        label: 'Observação',
        type: 'textarea',
        rows: 2,
        size: 12,
        required: true,
      },
    ];

    return fields;
  }

  private get currentMode() {
    let actualMode: AprovacaoVariasSolicitacoesMode = this.modes.find((mode) => mode.default);
    this.modes.map((mode) => actualMode = mode.condition(this.solicitacoes[0]) ? mode : actualMode);
    return actualMode;
  }

  private get isSolicitacaoProcesso() {
    return this.solicitacoes[0].NRTIPOSOLICITACAO === TiposSolicitacao.TP_SOLI_PESSOAL;
  }

  private get isSolicitacaoOnLastStep() {
    return !this.solicitacoes[0].ID_FASE_SEGUINTE_EXISTS || this.solicitacoes[0].ID_FASE_SEGUINTE_EXISTS === 'N';
  }

  private get isSolicitacaoProcessoOnLastStep() {
    return this.isSolicitacaoProcesso && this.isSolicitacaoOnLastStep;
  }

  private get genericFilter() {
    const filter = {
      NRORG: this.$store.getters.user.nrorg,
    };

    return filter;
  }

  private get requestRow(): SolicitacaoAprovacao[] {
    return this.buildExtraRowMapped();
  }

  // #endregion

  // #region [ WATCHERS ]
  @Watch('value', { immediate: true })
  private valueOnChange() {
    this.internalValue = this.value;

    this.row = { ...this.solicitacoes };
  }
  @Watch('type', { immediate: true })
  private typeOnChange() {
    if(this.type === 'A') {
      this.modes.push(
        {
          name: 'aprovacao',
          labels: {
            save: 'Aprovar',
            title: 'Aprovação de solicitação',
            finalStepTitle: 'Aprovação de solicitação',
          },
          save: this.aprovaSolicitacao,
          condition: (solicitacoes: SolicitacaoAprovacao) => true,
          default: true,
        }
      );
    }

    if(this.type === 'R') {
      this.modes.push(
        {
          name: 'reprovacao',
          labels: {
            save: 'Reprovar',
            title: 'Reprovação de solicitação',
            finalStepTitle: 'Reprovação de solicitação',
          },
          save: this.reprovaSolicitacao,
          condition: (solicitacoes: SolicitacaoAprovacao) => true,
          default: true,
        }
      );
    }
  }
  // #endregion

  // #region [ METHODS ]
  private closePopup() {
    this.internalValue = false;

    this.clearRow();
    this.emitInput(this.internalValue);
  }

  private closePopupOnSuccess() {
    this.closePopup();
    this.emitClose();
  }

  private async save() {
    if (!(this.row as any).validate()) {
      SnackbarUtils.showMessage(Messages.REQUIRED_FIELDS_NOT_FILLED, SnackbarUtils.TYPE_ALERT);
      return;
    }

    this.loading = true;

    try {
      const saveSuccess = await this.currentMode.save(this.requestRow);
      if (saveSuccess) {
        this.closePopupOnSuccess();
      }
    } finally {
      this.loading = false;
    }
  }

  private async aprovaSolicitacao(row: SolicitacaoAprovacao[]): Promise<boolean> {
    try {
      const dataRows = this.buildaRow(this.solicitacoes, row);

      if(!dataRows.length) {
        SnackbarUtils.showMessage(Messages.SOLI_APROVA_VAZIO, SnackbarUtils.TYPE_ALERT);
        return;
      }
      
      const response = await SolicitacaoController.aprovaVariasSolicitacoes(dataRows);
      if (response.data && response.data.messages) {
        SnackbarUtils.showMessage(response.data.messages[0].message, SnackbarUtils.TYPE_ALERT);
        return;
      }

      return true;
    } catch (err) {
      const errResponse = (err as any).response;
      const message = errResponse && errResponse.data ? errResponse.data.error : Messages.SOLI_APROV_ERROR;
      SnackbarUtils.showMessage(message, SnackbarUtils.TYPE_ERROR);
    }

    return false;
  }

  private async reprovaSolicitacao(row: SolicitacaoAprovacao[]): Promise<boolean> {
    try {
      const dataRows = this.buildaRow(this.solicitacoes, row);

      if(!dataRows.length) {
        SnackbarUtils.showMessage(Messages.SOLI_APROVA_VAZIO, SnackbarUtils.TYPE_ALERT);
        return;
      }
      
      const response = await SolicitacaoController.reprovaVariasSolicitacoes(dataRows);
      if (response.data && response.data.messages) {
        SnackbarUtils.showMessage(response.data.messages[0].message, SnackbarUtils.TYPE_ALERT);
        return;
      }

      return true;
    } catch (err) {
      const errResponse = (err as any).response;
      const message = errResponse && errResponse.data ? errResponse.data.error : Messages.SOLI_APROV_ERROR;
      SnackbarUtils.showMessage(message, SnackbarUtils.TYPE_ERROR);
    }

    return false;
  }

  private clearRow() {
    this.row = { ...this.rowDefaultValue };
  }

  private buildExtraRowMapped(): any {
    const extraRow = {};
    const rowCopy = this.row;

    this.extraRowFields.map((column) => {
      extraRow[column] = rowCopy[column];
      delete rowCopy[column];
    });

    const row: SolicitacaoAprovacao = {
      ...rowCopy, 
      EXTRA_ROW: { ...extraRow },
      NRSOLICITACAO: '',
      NRTIPOSOLICITACAO: '',
      NRVINCULOM: '',
      NMVINCULOM: '',
      NMFASEAPROVSOLI: '',
      NMTIPOSOLICITACAO: '',
    };
    return row;
  }

  private buildaRow(rows: any[], popupRow: any): SolicitacaoAprovacao[] {
    const extraRow = {};

    const rowsFiltered = rows.filter((row) => 
      row.ID_OPER_APROVA_SOLI == 'S' && 
      (row.IDAPROVACAOSEC != 'A' && row.IDAPROVACAOSEC != 'R')
    );

    const rowCopy = rowsFiltered.map((row, index) => {
      const extraRow = {};
      this.extraRowFields.forEach((column) => {
        extraRow[column] = row[column];
        delete row[column];
      });
    
      const dado = {
        NRORG: store.getters.user.nrorg,
        NRSOLICITACAO: row.NRSOLICITACAO,
        NRTIPOSOLICITACAO: row.NRTIPOSOLICITACAO,
        NRVINCULOM: row.NRVINCULOM,
        NMVINCULOM: row.NMVINCULOM,
        NMFASEAPROVSOLI: row.NMFASEAPROVSOLI,
        NMTIPOSOLICITACAO: row.NMTIPOSOLICITACAO,
        ID_OPER_APROVA_SOLI: row.ID_OPER_APROVA_SOLI,
        CDOPERADOR_APROVANTE: store.getters.user.cdoperador,
        DSOBSERVACAO: popupRow.DSOBSERVACAO,
        EXTRA_ROW: extraRow
      };
    
      return dado;
    });

    return rowCopy;
  }
  
  // #endregion
}

export default AprovacaoVariasSolicitacoes;
